import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Upload } from "lucide-react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ImageUploadIcon,
  MusicNote01Icon,
  Video01Icon,
  FileAttachmentIcon,
} from "@hugeicons/core-free-icons";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { createAssetsApi } from "@/lib/api/assets";
import { cn } from "@/lib/utils";
import type { Asset, AssetType } from "@/lib/types/assets";

type SortMode = "recently-used" | "most-used" | "newest" | "oldest";

const SORT_LABELS: Record<SortMode, string> = {
  "recently-used": "Recently Used",
  "most-used": "Most Used",
  newest: "Newest",
  oldest: "Oldest",
};

const TYPE_ICONS = {
  image: ImageUploadIcon,
  audio: MusicNote01Icon,
  video: Video01Icon,
  document: FileAttachmentIcon,
};

export interface AssetPickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  acceptType?: AssetType;
  disabledTypes?: AssetType[];
  onSelect: (asset: Asset) => void;
}

// Thumbnail shown in the list row
function AssetThumb({
  asset,
  assetsApi,
}: {
  asset: Asset;
  assetsApi: ReturnType<typeof createAssetsApi>;
}) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (asset.assetType !== "image") return;
    let objectUrl: string | null = null;
    assetsApi
      .fetchContent(asset.id)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, asset.assetType, assetsApi]);

  const Icon = TYPE_ICONS[asset.assetType] ?? FileAttachmentIcon;

  if (url) {
    return <img src={url} alt="" className="w-full h-full object-cover" />;
  }
  return (
    <div className="w-full h-full flex items-center justify-center">
      <HugeiconsIcon
        icon={Icon}
        size={14}
        className="text-muted-foreground/50"
      />
    </div>
  );
}

// Large preview shown in the right panel
function AssetPreview({
  asset,
  assetsApi,
}: {
  asset: Asset;
  assetsApi: ReturnType<typeof createAssetsApi>;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (asset.assetType === "document") return;
    setUrl(null);
    setLoading(true);
    let objectUrl: string | null = null;
    assetsApi
      .fetchContent(asset.id)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id, asset.assetType, assetsApi]);

  if (loading) {
    return (
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground/40" />
    );
  }

  if (asset.assetType === "image" && url) {
    return (
      <img
        src={url}
        alt={asset.name}
        className="max-w-full max-h-full object-contain rounded-xl"
      />
    );
  }

  if (asset.assetType === "audio" && url) {
    return (
      <div className="flex flex-col items-center gap-4 px-6">
        <HugeiconsIcon
          icon={MusicNote01Icon}
          size={48}
          className="text-muted-foreground/30"
        />
        <p className="text-sm font-medium text-muted-foreground">
          {asset.name}
        </p>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls src={url} className="w-full max-w-sm" />
      </div>
    );
  }

  if (asset.assetType === "video" && url) {
    return (
      // eslint-disable-next-line jsx-a11y/media-has-caption
      <video controls src={url} className="max-w-full max-h-full rounded-xl" />
    );
  }

  // Document or fallback
  const Icon = TYPE_ICONS[asset.assetType] ?? FileAttachmentIcon;
  return (
    <div className="flex flex-col items-center gap-3">
      <HugeiconsIcon
        icon={Icon}
        size={48}
        className="text-muted-foreground/30"
      />
      <p className="text-sm font-medium text-muted-foreground">{asset.name}</p>
    </div>
  );
}

export function AssetPickerSheet({
  open,
  onOpenChange,
  acceptType,
  disabledTypes,
  onSelect,
}: AssetPickerSheetProps) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("newest");
  const [selected, setSelected] = useState<Asset | null>(null);

  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);

  const { data: assets = [], isLoading } = useQuery({
    queryKey: ["assets", acceptType ?? "all", ""],
    queryFn: () => assetsApi.list({ type: acceptType }),
    enabled: open,
    staleTime: 60_000,
  });

  // Clear selection when dialog opens
  useEffect(() => {
    if (open) setSelected(null);
  }, [open]);

  const filtered = useMemo(() => {
    let list = assets;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((a) => a.name.toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      switch (sortMode) {
        case "oldest":
          return (
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          );
        case "recently-used":
          return (
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
          );
        case "most-used":
        case "newest":
        default:
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );
      }
    });
  }, [assets, search, sortMode]);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => assetsApi.upload(file),
    onSuccess: (asset) => {
      queryClient.invalidateQueries({ queryKey: ["assets"] });
      onSelect(asset);
      onOpenChange(false);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const isDisabledType = disabledTypes?.some((t) =>
        file.type.startsWith(`${t}/`),
      );
      if (!isDisabledType) {
        uploadMutation.mutate(file);
      }
    }
    e.target.value = "";
  };

  const handleConfirm = () => {
    if (!selected) return;
    onSelect(selected);
    onOpenChange(false);
  };

  const acceptAttr = (() => {
    if (acceptType) return `${acceptType}/*`;
    const parts: string[] = [];
    if (!disabledTypes?.includes("image")) parts.push("image/*");
    if (!disabledTypes?.includes("audio")) parts.push("audio/*");
    if (!disabledTypes?.includes("video")) parts.push("video/*");
    parts.push(".pdf,.txt,.md");
    return parts.join(",");
  })();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col p-0 gap-0 max-w-[860px] sm:max-w-[860px] w-[95vw] h-[620px] overflow-hidden border-border/50">
        {/* Top bar: search + sort */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 shrink-0">
          {/* Search with upload suffix */}
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search for Assets"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={cn(
                "w-full h-11 rounded-full bg-muted/60 border border-transparent pl-5 pr-12",
                "text-sm placeholder:text-muted-foreground/50",
                "outline-none focus:bg-muted/80 focus:border-border/60 transition-colors",
              )}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              title="Upload file"
              className="absolute right-3 top-1/2 -translate-y-1/2 flex size-7 items-center justify-center rounded-full hover:bg-muted/80 transition-colors cursor-pointer"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="h-4 w-4 text-muted-foreground" />
              )}
            </button>
          </div>

          {/* Sort dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex w-[145px] shrink-0 cursor-pointer items-center justify-between gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors">
                {SORT_LABELS[sortMode]}
                <svg
                  className="h-4 w-4 opacity-60"
                  viewBox="0 0 16 16"
                  fill="none"
                >
                  <path
                    d="M4 6l4 4 4-4"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuRadioGroup
                value={sortMode}
                onValueChange={(v) => setSortMode(v as SortMode)}
              >
                <DropdownMenuRadioItem value="recently-used">
                  Recently Used
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="most-used">
                  Most Used
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="newest">
                  Newest
                </DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="oldest">
                  Oldest
                </DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          <input
            ref={fileInputRef}
            type="file"
            accept={acceptAttr}
            className="sr-only"
            onChange={handleFileChange}
          />
        </div>

        {/* Two-panel body */}
        <div className="flex flex-1 gap-3 px-4 pb-4 min-h-0">
          {/* Left: scrollable asset list */}
          <div className="w-[340px] shrink-0 rounded-xl bg-muted/20 border border-border/30 overflow-hidden">
            <ScrollArea className="h-full">
              <div className="p-2 flex flex-col gap-0.5">
                {isLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/40" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center gap-1.5">
                    <p className="text-sm text-muted-foreground/50">
                      No assets found
                    </p>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-xs text-muted-foreground/40 hover:text-muted-foreground transition-colors cursor-pointer mt-1"
                    >
                      Upload one
                    </button>
                  </div>
                ) : (
                  filtered.map((asset) => {
                    const isDisabled = disabledTypes?.includes(asset.assetType) ?? false;
                    return (
                      <button
                        key={asset.id}
                        onClick={() => { if (!isDisabled) setSelected(asset); }}
                        onDoubleClick={() => {
                          if (isDisabled) return;
                          setSelected(asset);
                          onSelect(asset);
                          onOpenChange(false);
                        }}
                        className={cn(
                          "flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors",
                          isDisabled
                            ? "opacity-40 cursor-not-allowed pointer-events-none"
                            : cn(
                                "cursor-pointer",
                                selected?.id === asset.id
                                  ? "bg-muted/80 text-foreground"
                                  : "text-foreground/75 hover:bg-muted/50 hover:text-foreground",
                              ),
                        )}
                      >
                        <div className="size-9 shrink-0 overflow-hidden rounded-lg border border-border/20 bg-muted/50">
                          <AssetThumb asset={asset} assetsApi={assetsApi} />
                        </div>
                        <span className="truncate text-sm font-medium flex-1">
                          {asset.name}
                        </span>
                        {asset.source === "generated" && (
                          <span className="shrink-0 rounded-full bg-[--brand]/10 px-1.5 py-0.5 text-[10px] font-medium text-[--brand] leading-none">
                            AI
                          </span>
                        )}
                      </button>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right: preview */}
          <div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl bg-muted/20 border border-border/30">
            {selected ? (
              <AssetPreview asset={selected} assetsApi={assetsApi} />
            ) : (
              <p className="text-sm text-muted-foreground/35 select-none">
                Select an asset to preview
              </p>
            )}
          </div>
        </div>

        {/* Footer confirm — only when something is selected */}
        {selected && (
          <div className="flex items-center justify-end gap-2 px-4 pb-4 shrink-0">
            <button
              onClick={() => setSelected(null)}
              className="cursor-pointer rounded-lg px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              className="cursor-pointer rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background transition-opacity hover:opacity-90"
            >
              Use this asset
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
